import {Hero} from '@primer/react-brand/esm'

import {getImageSources, MAX_CONTENT_WIDTH} from '@github-ui/swp-core/lib/utils/images'
import {clsx} from 'clsx'

import {AssetTypeEnum} from '../../types'
import {AssetVideo} from '../Video'
import type {FlexSuiteFiftyFiftyHeroProps} from './types'
import {HeroContent} from './HeroContent'
import {useDitherBackground} from '../../hooks/useDitherBackground'
import {hasExternalVideo} from '../../utils/asset-helpers'
import styles from './FlexSuiteHero.module.css'

/**
 * 50/50 hero: content on one side with an inline-end + padded image (or video)
 * on the other. Modeled on Primer Brand's `GridlineImageInlineEndPadded`
 * gridline variant.
 */
export function FlexSuiteHero5050({fields}: {fields: FlexSuiteFiftyFiftyHeroProps}) {
  const {heroAsset} = fields
  const {assetType, optionImage, optionVideo, optionPoster, autoplay, videoSource} = heroAsset?.fields ?? {}
  const externalVideo = hasExternalVideo(assetType, videoSource)
  const showMediaDither = !externalVideo
  const {containerRef, DitherBackground} = useDitherBackground({
    enabled: showMediaDither,
  })

  return (
    <>
      <HeroContent
        {...fields}
        className={clsx(showMediaDither && styles.ditherContainer)}
        imageContainerClassName={clsx(
          styles.heroImageWrapper,
          externalVideo && styles.externalVideoGradient,
          fields.imageContainerClassName,
        )}
        imageContainerRef={containerRef as unknown as React.RefObject<HTMLDivElement | null>}
        data-testid="hero-50-50"
      >
        {assetType === AssetTypeEnum.enum.Image && optionImage && (
          <Hero.Image
            as="picture"
            position="inline-end"
            padding="all"
            src={`${optionImage.fields.file.url}?fm=webp`}
            sources={getImageSources(optionImage.fields.file.url, {
              maxWidth: MAX_CONTENT_WIDTH / 2,
            })}
            alt={optionImage.fields.description ?? ''}
            fetchPriority="high"
          />
        )}
        {assetType !== AssetTypeEnum.enum.Image && (optionVideo || videoSource) && (
          <Hero.Video position="inline-end" padding="all">
            <AssetVideo
              assetType={assetType}
              optionVideo={optionVideo}
              optionPoster={optionPoster}
              autoplay={autoplay}
              videoSource={videoSource}
              priority="high"
            />
          </Hero.Video>
        )}
      </HeroContent>
      <DitherBackground />
    </>
  )
}
